import debounce from 'lodash/debounce';
import analytics from './../analytics.js';
import $ from 'jquery';
import LoadingIcon from '../generic/loading-icon.js';
import Mustache from 'mustache';

/**
 * Filters used in the Fiksudiili search
 * @type {Array}
 * @property {string} id - element id
 * @property {string} name - element name
 * @property {string} type - element type
 * @property {boolean} dynamicValues - element has dynamic values that are updated from the server side
 */
const filters = [
  { id: 'filter-brands', 'name': 'brands[]', type: 'multiselect'},
  { id: 'filter-models', 'name': 'models[]', type: 'multiselect'},
  { id: 'car-type', 'name': 'car-type[]', type: 'multiselect'},
  { id: 'filter-fuel', 'name': 'fuel', type: 'multiselect', dynamicValues: true },
  { id: 'filter-gear', 'name': 'gear[]', type: 'multiselect'},
  { id: 'brands-hidden-filter', name: 'brands[]', type: 'hidden' },
  { id: 'models-hidden-filter', name: 'models[]', type: 'hidden' },
  { id: 'bodyType', name: 'body_type[]', type: 'multiselect', dynamicValues: true },

  // Main search new filters
  { id: 'car-category', name: 'car_category', type: 'multiselect' },
  { id: 'car-body-type', name: 'car-body-type', type: 'multiselect' },
];

class FiksudiiliSearch {

  /**
   * @constructor
   * @param {HTMlElement} element element bind FiksudiiliSearch logic
   */
  constructor( element ) {

    this._element = element;
    this._elements(element);
    this._bind();

    //Page attribute is used to control StageQL limit clause, amount of search result in one set can be changed on the server side.
    this._page = 0;

    //Debounced version of the search method, just for recuding unnecessary calls to the server side. Multiselect element has no way to prevent
    //selection event in situation when option are selected programatically. This an issue specially with the brand & model multiselect elements.
    this.debouncedSearch = debounce(this.search, 100, { trailing: true, leading: false });

    //This for infinite scroll
    this._initIntersectionObserver();

    this._showModels();

    //Expected search results maximum amount
    this.searchResultsAmount = parseInt( this._element.dataset.searchresultamount );
  }

  /**
   * Preload elements from to DOM to be more accessable
   * @param {HTMLElement} element element passed in the constructor
   */
  _elements(element) {
    this._form = element.querySelector('form');
    this._carCardTemplateParts = JSON.parse( element.querySelector('.mustache-calculator-car-card').innerText );
    this._carSearchResults = element.querySelector('.fiksudiili-search-search-result .cars');
    this._storageCheckbox = element.querySelector('input[name="storage"]');
    this._offercarsCheckbox = element.querySelector('input[name="offercars"]');
    this._categories = element.querySelectorAll('input[type="radio"][name="category"]');
    this._fetchMoreBtn = element.querySelector('#fetch-more-button button');
    this._filterHandle = document.querySelector('.fiksudiili-search-filter-handle');
    this._intersectionObservableElement = document.querySelector('.fiksudiili-search-footer');
    this._noMoreSearchResult = document.querySelector('.no-more-search-result');
    this._extraLinks = element.querySelector('.extra-links');
    this._resetBtn = element.querySelector('button[type="reset"]');
    this._hideButton = element.querySelector('button.hide-button');
    this._hideButtonHolder = element.querySelector('.hide-button-holder');
    this._filters = element.querySelector('.filters');
    this._rangeSlider = element.querySelector('.js-range-slider');
    this._tabs = element.querySelector('.tabs');
    this._seoTemplate = element.querySelector('#mustache-seo-container')?.textContent;

    // New search elements
    this._searchButton = document.getElementById('run-hd-search');
    this._transmissionManual = element.querySelector('input[name="transmission-manual"]');
    this._transmissionAutomatic = element.querySelector('input[name="transmission-automatic"]');
    this._driveTypeFwd = element.querySelector('input[name="drive-type-awd"]');
    this._categoryFilters = element.querySelectorAll('input[name="car_category[]"]');

    this._hiddenFilterElements = Object.keys( filters )
      .filter( key => filters[key].type === 'hidden' )
      .map( key => {
        return element.querySelector(`input[name="${filters[key].name}"]`);
      }).filter( Boolean );
  }

  /**
   * Does a car search to the Stage
   *
   * @async
   * @param {object} options options object
   * @param {boolean} options.append  default false, appends carsearch__results are instead overwriting it
   */
  async search(options = {}) {

    //Stop intersection observation during the search, prevent stopping search if user scroll up and down, up and down at the bottom of the page
    this._stopIntersectionObservation();

    //Each search has it's own AbortController isntance so they don't mixup between request.
    this._controller = new window.AbortController();

    //Get request's is formed by using this
    const formData = this._getSearchURL();

    try {
      const searchGetParameters = formData.toString();

      //Delete things that are not needed in the browser's URL address bar
      formData.delete('currentBrand');
      formData.delete('page');

      history.pushState(null, null, '?' + formData.toString());

      this.loading = true; //Spinning the spinners

      //Send request and wait a moment for response and json.
      const response = await this._send(searchGetParameters, this._controller);
      const responseJSON = await response.json();

      const html = responseJSON.cars
        .map(car => {
          const carCardTemplateParts = this._carCardTemplateParts[car.cardtype];
          car.cardCustomClasses = carCardTemplateParts.cardCustomClasses;

          if( car?.labels?.some( label => label?.name?.length > 0 ) ) {
            car.cardCustomClasses += ' card--label';
          }

          if(car.cardtype === 'fiksudiiliSearchModelCard') {
            const formData = new FormData(this._form);


            const categories = formData.getAll('car_category[]');

            // https://crasman.atlassian.net/browse/LAAKJK-3305 - Hedin Drive päähausta mallin sivulle - Jos kategorioita useampi, valitaan aina tilausautot
            // If there is more than one category selected. Always only allow only "tilausautot" -category.
            if( categories.length > 1 ) {
              formData.delete('car_category[]');
              formData.set('car_category[]', '1');
            }

            const urlParams = new URLSearchParams(formData).toString();

            car.listlink += '?' + urlParams;
          }

          return window.Dali.render(carCardTemplateParts.template, car, carCardTemplateParts.partials);
        })
        .join(' ');

      if (options.append) {
        this._carSearchResults.insertAdjacentHTML('beforeend', html);
      } else {
        this._carSearchResults.innerHTML = html;
      }


      //Can't be placed to finally block due "AbortError", abort error is thrown to stop last fetch
      this.loading = false;

      //Operation performed after each succesfull search is done there..
      this._afterSearch(responseJSON);

    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Fetch aborted', error); //This occures when a AbortController aborts fetch.
      } else {
        console.warn('Unhandled error:', error);
      }
    }
  }

  /**
   * Setter for the loading attribute. Shown or hides the loading spinner when car search is loading.
   * @param {boolean} value true is loading false if not
   */
  set loading(value) {
    this._loading = value;
    this._showFetchMoreBtn(!value);
    LoadingIcon.instances['fiksudiili-loading-icon'].show = value;
  }

  // ---- Private functions ----

  /**
   * Send ajax request via fetch API to the Stage
   * @async
   * @param {string} formData get parameters
   * @param {AbortController} controller abort controller signal object
   * @return {Promise} resolved after successful call
   */
  async _send(formData, controller) {
    return await fetch(this._getBaseURL() + formData, {
      method: 'GET',
      signal: controller.signal,
    });
  }

  #hideButtonClicked() {
    this._filters.classList.toggle('hidden');
    const isHidden = this._filters.classList.contains('hidden');

    this._hideButton.classList.toggle('hedin-btn--ghost', !isHidden);
    this._hideButton.classList.toggle('hedin-btn--default', isHidden);
    this._hideButton.classList.toggle('f-icon--arrow-up', !isHidden);

    this._hideButton.classList.toggle('f-icon--filter', isHidden);

    const filtersCount =  window.multiselects?.['filter-brands'].getValue().length +
                          window.multiselects?.['filter-fuel'].getValue().length +
                          window.multiselects?.['car-type'].getValue().length;

    this._hideButtonHolder.classList.toggle('bg--ui-07', !isHidden);
    this._hideButtonHolder.classList.toggle('bg--ui-05', isHidden);

    this._hideButtonHolder.classList.toggle('height-0', isHidden);

    this._hideButton.innerText = isHidden ? `Näytä hakusuodattimet (${filtersCount})` : 'Piilota hakusuodattimet';
  }

  /**
   * Aborts last made search before the next search is started.
   */
  _abortSearch() {
    if (this._controller) {
      this._controller.abort();
    }
    this.loading = false;
  }

  /**
   * Bind event listeners to HTMLelements
   */
  _bind() {

    //This way we create only one copy of these functions
    const onSelectChanges = this._onSelectChanges.bind(this);
    const searchButtonPressed = this._runSearch.bind(this);
    const onAnalyticCategoryChanges = this._analyticsCategoryChanges.bind(this);

    filters.filter(filter => filter.type === 'multiselect').forEach(filter => {
      window.multiselects?.[filter.id]?.addCallback({ onValueChange: onSelectChanges });
    });

    this._fetchMoreBtn?.addEventListener('click', async () => {
      await this._fetchMoreSearchResults();
      this._fetchMoreBtn.classList.add('hidden');
      this._startInterSectionObservation();
    });

    this._filterHandle?.addEventListener('click', this._onFilterHandleClicked.bind(this));
    this._form.addEventListener('reset', this._onReset.bind(this));
    this._hideButton?.addEventListener('click', this.#hideButtonClicked.bind(this));

    if(this._tabs) {
      this._tabs.addEventListener('click', this._onTabChanges.bind(this));
    }

    // If search button does not exists, bind click event to it
    // This is used in the FiksudiiliModelPage where search button does not exist.
    if(!this._searchButton) {
      this._storageCheckbox?.addEventListener('change', searchButtonPressed );

      this._offercarsCheckbox?.addEventListener('change', searchButtonPressed );

      this._categories.forEach( categoryRadioInput => {
        categoryRadioInput.addEventListener('click', searchButtonPressed )
        categoryRadioInput.addEventListener('click', onAnalyticCategoryChanges );
      });

      if(this._rangeSlider) {
        this._onRangeSliderChange = debounce(this._onRangeSliderChange.bind(this), 500 );
        this._rangeSlider.rangeSlider.addChangeListener(this._onRangeSliderChange)
      }
    } else {
      //If search button exists, bind click event to it
      this._searchButton.addEventListener('click', searchButtonPressed);
    }
  }

  _onTabChanges(event) {
    const { target } = event;

    if( target.tagName === 'BUTTON' && target.classList.contains('tabs__tab')) {
      this._runSearch();
    }
  }


  _onRangeSliderChange(value) {
    this._runSearch();
  }

  /**
   * Fetch more search results.
   */
  async _fetchMoreSearchResults() {
    this._page += 1;
    await this.search({ append: true });
  }

  _onFilterHandleClicked() {
    $('.fiksudiili-filters-container,.fiksudiili-filters').slideToggle(300);
    const classTarget = document.querySelector('.fiksudiili-search-filter-handle')?.dataset?.collapseClassTarget;
    document.querySelector(classTarget)?.classList?.toggle('collapsed');
  }

  /**
   * On form's reset event
   * @param {Event} event reset event
   */
  _onReset(event) {
    event.preventDefault();

    filters.filter( filter => filter.type === 'multiselect' ).forEach( filter => {
      window.multiselects?.[filter.id]?.unSelectAll(true);
    });

    this._storageCheckbox = false;
    this._offercarsCheckbox = false;
  }

  /**
   * Filters car models multiselect option groups depending what brands are selected.
   */
  _showModels() {
    const allBrands = window.multiselects?.['filter-models']?.listAllGroupNames();
    const selectedBrands = window.multiselects?.['filter-brands']?.getValue();

    if (Array.isArray(selectedBrands)) {
      if (selectedBrands.length === 0) {
        window.multiselects?.['filter-models']?.unSelectAll(false);
        window.multiselects?.['filter-models']?.disableMultiselect();
      } else {
        window.multiselects?.['filter-models']?.enableMultiselect();
      }
    }

    allBrands?.forEach(brandSlug => {
      const show = selectedBrands.includes(brandSlug);
      window.multiselects?.['filter-models'].toggleOptionGroup(brandSlug, show);
    });

  }

  /**
   * When a category checkbox value changes this is called.
   */
  _runSearch() {
    this._page = 0;
    this._stopIntersectionObservation();
    this._fetchMoreBtn.classList.remove('hidden');
    this._emptySearchResults();
    this._showNoMoreSearchResult(false);
    this._showFetchMoreBtn(true);
    this._abortSearch();
    this.debouncedSearch();
  }

  /**
   * Empty search result area from loaded cars
   */
  _emptySearchResults() {
    this._carSearchResults.innerHTML = '';
  }

  /**
   * Build search url address
   * @return {URLSearchParams} formData
   */
  _getSearchURL() {

    const formData = new URLSearchParams();

    filters.filter( filter => filter.type === 'multiselect' ).forEach( filter => {

      const value = window.multiselects?.[ filter.id ]?.getValue();

      if( value && value.length > 0 ) {
        value.forEach(value => {
          formData.append(filter.name, value);
        });
      }
    });

    if( this._tabs?.tabs?.isTabOpen('STORAGE_CARS') ) {
      formData.append('category', 'STORAGE_CARS' );
    } else if( this._tabs?.tabs?.isTabOpen('FACTORY_CARS') ) {
      formData.append('category', 'FACTORY_CARS' );
    } else if( this._tabs?.tabs?.isTabOpen('USED_CARS') ) {
      formData.append('category', 'USED_CARS' );
    }

    if( this._form?.category?.value?.length > 0 ) {
      formData.append('category', this._form.category.value );
    }

    if( this._offercarsCheckbox?.checked ) {
      formData.append('offercars', true);
    }

    if( this._rangeSlider ) {
      const value = this._rangeSlider.rangeSlider.value;

      formData.append('minmontly', value.min);
      formData.append('maxmontly', value.max);
    }

    //This is used by FiksudiiliModelPage brand & model
    this._hiddenFilterElements.forEach( hiddenFilterElement => {
      formData.append(hiddenFilterElement.name, hiddenFilterElement.value);
    });

    this._categoryFilters.forEach(checkbox => {
      if (checkbox.checked) {
        formData.append('car_category[]', checkbox.value);
      }
    });

    if( this._transmissionManual?.checked ) {
      formData.append('gear[]', this._transmissionManual.value);
    }

    if( this._transmissionAutomatic?.checked ) {
      formData.append('gear[]', this._transmissionAutomatic.value);
    }

    if( this._driveTypeFwd?.checked ) {
      formData.append('driveType', 'AWD');
    }

    //Currentbrand add more filters like locations on the Stage side based on current brand.
    formData.append('currentBrand', document.body.dataset.brand);
    formData.append('page', this._page );

    return formData;
  }

  /**
   * Fetch request base URL-address
   * @return {string} ajax request base url
   */
  _getBaseURL() {
    return this._form.getAttribute('action') ?? '/stage-ajax/fiksudiili-search/?';
  }

  /**
   * Called after search
   * @param {object} responseJSON Fiksudiili search response object
   */
  _afterSearch(responseJSON) {
    //if( responseJSON.cars.length === 0 || responseJSON.cars.length < this.searchResultsAmount ) {'
    if( responseJSON.cars.length === 0 ) {
      this._showNoMoreSearchResult(true);
      this._showFetchMoreBtn(false);
      this._stopIntersectionObservation();
    } else {
      this._startInterSectionObservation();
      this._showNoMoreSearchResult(false);
    }

    this._updateSeoText(responseJSON.seoTexts);
    this._updateFilters(responseJSON);

    this._extraLinks.classList.toggle('hidden', !responseJSON.showStorageSearchLink)

    this._analytisFiksudiiliSearchEvent();
  }

  /**
   * Update multiselect filters with new values from the server side
   * @param {object} responseJSON - response from the server
   */
  _updateFilters(responseJSON) {

    Object.entries(responseJSON.filters).forEach( ([filterName, filter]) => {

      const filterOptions = filters.find( filter => filter.name === filterName || filter.id === filterName);
      const multiselect = window.multiselects?.[filterOptions?.id];

      if(filterOptions?.dynamicValues && multiselect) {

        const options = filter.options.map((option) => ({ shownValue: option.text, ...option }));
        multiselect.removeAllOptions(false,true);
        multiselect.addOptions(options);
      }
    });
  }

  /**
   * When a change event happens in any multiselect element
   * this function is called.
   * @param {object} multiselectData selected data
   */
  _onSelectChanges(multiselectData) {
    if (multiselectData.element.id === 'filter-brands') {
      this._onBrandChanges();
    }

    if (!this._searchButton) {
      this._page = 0;

      this._emptySearchResults();
      this._showNoMoreSearchResult(false);
      this._showFetchMoreBtn(true);
      this._stopIntersectionObservation();
      this._fetchMoreBtn.classList.remove('hidden');
      this._abortSearch();
      this.debouncedSearch();
      this._analyticsMultiselectValueChanges(multiselectData);
    }
  }

  /**
   * Called when a brand multiselect values changes
   */
  _onBrandChanges() {
    this._showModels();
  }

  /**
   * Show or hide fetch more button bottom of the search results
   * @param {boolean} show true to show false to hide
   */
  _showFetchMoreBtn( show ) {
    this._fetchMoreBtn.classList.toggle('hidden', !show );
  }

  /**
   * Show or hide no more search result text bottom of the search results
   * @param {boolean} show true to show false to hide
   */
  _showNoMoreSearchResult( show ) {
    this._noMoreSearchResult.classList.toggle('hidden', !show );
  }

  /**
   * Intersection observer looks to the view port for a particular element, in this case it's the fetch more button element.
   * Observing is started after fetch more button is pressed once by the user, and stopped after after filter is changed or
   * all search results are fetched from the server.
   */
  _initIntersectionObserver() {
    this._observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if(entry.isIntersecting){
          this._fetchMoreSearchResults();
        }
      });

    }, {threshold: 1});
  }

  /**
   * Stop intersection observing, don't load more search result automatically when scrolling down.
   */
  _stopIntersectionObservation() {
    this._observer.unobserve(this._intersectionObservableElement);
  }

  /**
   * Start intersection observing
   */
  _startInterSectionObservation() {
    this._observer.observe(this._intersectionObservableElement);
  }

  _analyticsCategoryChanges( event ) {
    /*analytics.pushUIInteractionAnalytics({
      element: 'input',
      elementName: 'UI - Fiksudiili - filtermode | ' + (event.target.value || event.detail.value),
      type: 'change',
    });*/


  }

  _analyticsMultiselectValueChanges( multiselectData ) {
    const values = multiselectData.value.map( selectedValue => {
      const option = multiselectData.options.find( option => option.value === selectedValue );
      return option.shownValue;
    }).join(',')


    let name = multiselectData.element.dataset.analyticsName;

    analytics.pushUIInteractionAnalytics({
      element: 'input',
      elementName: `UI - Hedin Drive - ${name} | ${values}`,
      type: 'change',
    });
  }

  _analytisFiksudiiliSearchEvent() {

    let brand = window.multiselects?.['filter-brands']?.getValue().join(',');
    let model = window.multiselects?.['filter-models']?.getValue().join(',');

    if( this._hiddenFilterElements.length > 0 ) {
      brand = this._hiddenFilterElements.find( element => element.getAttribute('name') === 'brands[]' )?.value;
      model = this._hiddenFilterElements.find( element => element.getAttribute('name') === 'models[]' )?.value;
    }

    const [fuel] = window.multiselects?.['filter-fuel'].getValue();

    const searchData = {
      offercars: this._offercarsCheckbox?.checked,
      fuel: fuel ? fuel : '',
      brand,
      model,
    };

    if( this._tabs?.tabs?.isTabOpen('STORAGE_CARS') || this._tabs?.tabs?.isTabOpen('USED_CARS') ) {
      searchData.category = this._storageCheckbox?.checked ? 'storage' : ''
    }

    analytics.pushFiksudiiliSearchEvent(searchData);
  }

  _updateSeoText(seoTexts) {
    let seoContent = '';
    if (this._seoTemplate) {
      seoContent = Mustache.render(this._seoTemplate, {seoTexts});
    }

    const seoElement = this._intersectionObservableElement.querySelector('.hedin-drive-search-seo-container');
    if (seoElement) {
      seoElement.innerHTML = seoContent;
    }
  }

  // --- static function ---

  static init() {
    const fiksudiiliSearchElement = document.querySelector('.js-fiksudiili-search');

    if( fiksudiiliSearchElement ) {
      return new FiksudiiliSearch(fiksudiiliSearchElement);
    }
  }
}

export default FiksudiiliSearch;
